// =================================================================================
// File: src/contexts/PublicStudioContext.jsx
// Description: Manages public studio profiles for Mudo Map
// =================================================================================

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { supabase, hasSupabase } from "../supabaseClient";
import { useStudio } from "./StudioContext";
import { useAuth } from "./AuthContext";
import {
  getNominatimHeaders,
  applyNominatimContact,
} from "../utils/nominatim";

// Simple UUID validator
const isUuid = (val) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    val
  );

// Deterministically convert any string to UUIDv5 (using a fixed namespace)
function uuidFromString(str) {
  // Basic hash to UUID v5 style fallback (no external lib)
  const enc = new TextEncoder().encode(str);
  const buf = crypto.subtle
    ? crypto.subtle.digest("SHA-1", enc)
    : new Uint8Array(16);
  return Promise.resolve(buf).then((hash) => {
    const bytes = new Uint8Array(hash).slice(0, 16);
    bytes[6] = (bytes[6] & 0x0f) | 0x50; // version 5
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant RFC4122
    const hex = [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
    return (
      hex.slice(0, 8) +
      "-" +
      hex.slice(8, 12) +
      "-" +
      hex.slice(12, 16) +
      "-" +
      hex.slice(16, 20) +
      "-" +
      hex.slice(20)
    );
  });
}

const PublicStudioContext = createContext(null);
export const usePublicStudio = () => useContext(PublicStudioContext);

export function PublicStudioProvider({ children }) {
  // Safely handle when StudioContext might return null (visitor mode)
  const studioContext = useStudio();
  const studio = studioContext?.studio || null;
  const studioId = studioContext?.studioId || null;
  const { user: authUser } = useAuth() || {};
  
  const [publicProfile, setPublicProfile] = useState(null);
  const [allPublicStudios, setAllPublicStudios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /* -------------------------------------------------------------------------- */
  /* 🔹 Get a normalized UUID for Supabase operations                           */
  /* -------------------------------------------------------------------------- */
  const getUserUuid = useCallback(async () => {
    // Prefer the authenticated Supabase user id (matches RLS policies)
    if (authUser?.id && isUuid(authUser.id)) {
      return authUser.id;
    }

    let uuid = localStorage.getItem("mudoUserUuid");
    if (uuid && isUuid(uuid)) return uuid;

    let localId = localStorage.getItem("mudoUserId");
    if (!localId) {
      localId = Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem("mudoUserId", localId);
    }

    // Derive deterministic UUID from localId
    uuid = await uuidFromString(localId);
    localStorage.setItem("mudoUserUuid", uuid);
    console.log("[PublicStudio] Derived UUID:", uuid);
    return uuid;
  }, [authUser?.id]);

  /* -------------------------------------------------------------------------- */
  /* 🔹 Fetch own profile                                                       */
  /* -------------------------------------------------------------------------- */
  const fetchOwnProfile = useCallback(async () => {
    if (!hasSupabase() || !studioId) return;
    try {
      const userUuid = await getUserUuid();
      const { data, error: err, status } = await supabase
        .from("public_studios")
        .select("*")
        .eq("user_id", userUuid)
        .maybeSingle();

      if (err && status !== 406) {
        console.error("[PublicStudio] Error fetching own profile:", err);
        return;
      }

      if (status === 406 || !data) {
        console.warn("[PublicStudio] no studio row for user, treating as null");
        setPublicProfile(null);
      } else {
        setPublicProfile(data);
        console.log("[PublicStudio] Loaded own profile:", data);
      }
    } catch (err) {
      console.error("[PublicStudio] fetchOwnProfile error:", err);
    }
  }, [studioId, getUserUuid]);

  /* -------------------------------------------------------------------------- */
  /* 🔹 Fetch all public studios                                                */
  /* -------------------------------------------------------------------------- */
  const fetchAllPublicStudios = useCallback(async () => {
    if (!hasSupabase()) return setAllPublicStudios([]);

    try {
      setLoading(true);
      const { data, error: err } = await supabase
        .from("public_studios")
        .select("*")
        .eq("is_active_public", true);

      if (err) {
        console.error("[PublicStudio] Error fetching public studios:", err);
        setError(err.message);
        return;
      }

      setAllPublicStudios(data || []);
      console.log(`[PublicStudio] Loaded ${data?.length || 0} studios`);
    } catch (err) {
      console.error("[PublicStudio] fetchAllPublicStudios error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  /* -------------------------------------------------------------------------- */
  /* 🔹 Publish or Update Studio                                                */
  /* -------------------------------------------------------------------------- */
  const publishStudio = useCallback(
    async (profileData) => {
      if (!hasSupabase()) throw new Error("Supabase not configured");

      const userUuid = await getUserUuid();
      console.log("[PublicStudio] Using UUID:", userUuid);

      try {
        // Optional geocode
        let { lat, lng } = profileData;
        if ((!lat || !lng) && profileData.city && profileData.country) {
          try {
            const q = `${profileData.city}, ${profileData.country}`;
            const url = applyNominatimContact(
              new URL("https://nominatim.openstreetmap.org/search")
            );
            url.searchParams.set("q", q);
            url.searchParams.set("format", "json");
            url.searchParams.set("limit", "1");
            const resp = await fetch(url.toString(), {
              headers: getNominatimHeaders(),
            });
            const data = await resp.json();
            if (Array.isArray(data) && data[0]) {
              lat = Number(data[0].lat);
              lng = Number(data[0].lon);
            }
          } catch (geoErr) {
            console.warn("[PublicStudio] Geocoding failed", geoErr);
          }
        }

        const payload = {
          studio_id: studioId || "default",
          user_id: userUuid,
          display_name: profileData.displayName || studio?.name || "My Studio",
          address: profileData.address || null,
          lat,
          lng,
          city: profileData.city || null,
          country: profileData.country || null,
          country_code: profileData.countryCode || null,
          tags: profileData.tags || [],
          website: profileData.website || null,
          booking_url: profileData.bookingUrl || null,
          instagram: profileData.instagram || null,
          is_active_public:
            profileData.isActivePublic !== undefined
              ? profileData.isActivePublic
              : true,
        };

        console.log("[PublicStudio] Publishing payload:", payload);

        let data, err;
        try {
          const res = await supabase
            .from("public_studios")
            .upsert(payload, { onConflict: "studio_id" })
            .select()
            .single();
          data = res.data; err = res.error;
        } catch (e) {
          err = e;
        }

        // Fallbacks: handle unique constraint conflicts by updating instead of inserting
        if (err) {
          console.warn("[PublicStudio] Upsert failed, attempting manual update/insert:", err?.message || err);

          // 1) Try update by studio_id (unique constraint complained about studio_id)
          const existingByStudio = await supabase
            .from("public_studios")
            .select("*")
            .eq("studio_id", payload.studio_id)
            .maybeSingle();

          if (existingByStudio.error && existingByStudio.error.code !== "PGRST116") {
            throw existingByStudio.error;
          }

          if (existingByStudio.data) {
            const upd = await supabase
              .from("public_studios")
              .update(payload)
              .eq("studio_id", payload.studio_id)
              .select()
              .single();
            if (upd.error) throw upd.error;
            data = upd.data;
          } else {
            // 2) Try update by user_id if a row exists for this user
            const existingByUser = await supabase
              .from("public_studios")
              .select("*")
              .eq("user_id", userUuid)
              .maybeSingle();

            if (existingByUser.error && existingByUser.error.code !== "PGRST116") {
              throw existingByUser.error;
            }

            if (existingByUser.data) {
              const upd = await supabase
                .from("public_studios")
                .update(payload)
                .eq("user_id", userUuid)
                .select()
                .single();
              if (upd.error) throw upd.error;
              data = upd.data;
            } else {
              // 3) No existing rows — do a fresh insert
              const ins = await supabase
                .from("public_studios")
                .insert(payload)
                .select()
                .single();
              if (ins.error) throw ins.error;
              data = ins.data;
            }
          }
        }

        setPublicProfile(data);
        fetchOwnProfile();
        fetchAllPublicStudios();
        return data;
      } catch (err) {
        console.error("[PublicStudio] publishStudio error:", err);
        throw err;
      }
    },
    [studioId, studio, getUserUuid, fetchOwnProfile, fetchAllPublicStudios]
  );

  /* -------------------------------------------------------------------------- */
  /* 🔹 Unpublish (declared BEFORE setMapVisibility to avoid TDZ)               */
  /* -------------------------------------------------------------------------- */
  const unpublishStudio = useCallback(async () => {
    if (!hasSupabase()) throw new Error("Supabase not configured");

    // Use the row's owner if we have it (satisfies RLS), otherwise auth uid, otherwise fallback UUID
    const targetUserId = publicProfile?.user_id || authUser?.id || (await getUserUuid());
    const targetStudioId = publicProfile?.studio_id || studioId || null;
    const sid = targetStudioId || "default";
    const userUuid = targetUserId;
    console.log("[PublicStudio] Unpublishing", { targetUserId, targetStudioId, rowId: publicProfile?.id });

    try {
      // Attempt a single update with the most specific filter; if blocked, go straight to delete to avoid noisy 403s.
      const updatePayload = { is_active_public: false, lat: null, lng: null };
      let errUpdate = null;

      const matchFilter = publicProfile?.id && targetUserId ? { id: publicProfile.id, user_id: targetUserId } : null;
      const hasUserFilter = Boolean(targetUserId);
      const hasStudioFilter = Boolean(targetStudioId);

      if (matchFilter) {
        const { error } = await supabase.from("public_studios").update(updatePayload).match(matchFilter);
        errUpdate = error;
      } else if (hasUserFilter) {
        const { error } = await supabase.from("public_studios").update(updatePayload).eq("user_id", targetUserId);
        errUpdate = error;
      } else if (hasStudioFilter) {
        const { error } = await supabase.from("public_studios").update(updatePayload).eq("studio_id", targetStudioId);
        errUpdate = error;
      } else {
        errUpdate = new Error("No filter available for unpublish update");
      }

      // Last-resort: delete the row if update is blocked by RLS
      if (errUpdate) {
        console.warn("[PublicStudio] update blocked by RLS, attempting delete", errUpdate?.message || errUpdate);
        const deleteFilters = [
          hasUserFilter ? `user_id.eq.${targetUserId}` : null,
          hasStudioFilter ? `studio_id.eq.${targetStudioId}` : null,
        ].filter(Boolean);

        if (!deleteFilters.length) throw errUpdate;

        const { error: errDelete } = await supabase
          .from("public_studios")
          .delete()
          .or(deleteFilters.join(","));
        if (errDelete) throw errDelete;
      }

      setPublicProfile((p) =>
        p ? { ...p, is_active_public: false, lat: null, lng: null } : null
      );
      setAllPublicStudios((list) =>
        Array.isArray(list)
          ? list.filter(
              (s) =>
                String(s.user_id) !== String(userUuid) &&
                String(s.studio_id || "") !== String(sid)
            )
          : []
      );

      // Refresh to mirror remote state
      fetchOwnProfile();
      fetchAllPublicStudios();
    } catch (err) {
      console.error("[PublicStudio] unpublishStudio error:", err);
      throw err;
    }
  }, [getUserUuid, studioId, fetchOwnProfile, fetchAllPublicStudios]);

  /* -------------------------------------------------------------------------- */
  /* 🔹 Unified Map Visibility Setter (High-level toggle)                       */
  /* -------------------------------------------------------------------------- */
  const setMapVisibility = useCallback(
    async (isVisible, { source = "unknown" } = {}) => {
      console.debug("[PublicStudio] setMapVisibility:", { isVisible, studioId, source });
      
      try {
        if (isVisible) {
          // Publish with minimum required data
          const profileData = {
            displayName: publicProfile?.display_name || studio?.name || "My Studio",
            address: publicProfile?.address || null,
            city: publicProfile?.city || null,
            country: publicProfile?.country || null,
            tags: publicProfile?.tags || [],
            website: publicProfile?.website || null,
            bookingUrl: publicProfile?.booking_url || null,
            instagram: publicProfile?.instagram || null,
            lat: publicProfile?.lat || null,
            lng: publicProfile?.lng || null,
            isActivePublic: true,
          };
          return await publishStudio(profileData);
        } else {
          // Unpublish
          return await unpublishStudio();
        }
      } catch (err) {
        console.error("[PublicStudio] setMapVisibility failed:", err);
        throw err;
      }
    },
    [publicProfile, studio, publishStudio, unpublishStudio]
  );

  /* -------------------------------------------------------------------------- */
  /* 🔹 Initial + Realtime                                                      */
  /* -------------------------------------------------------------------------- */
  useEffect(() => {
    fetchOwnProfile();
    fetchAllPublicStudios();
  }, [fetchOwnProfile, fetchAllPublicStudios]);

  useEffect(() => {
    if (!hasSupabase()) return;
    const channel = supabase
      .channel("public_studios_changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "public_studios",
          filter: "is_active_public=eq.true",
        },
        () => fetchAllPublicStudios()
      )
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [fetchAllPublicStudios]);

  /* -------------------------------------------------------------------------- */
  /* 🔹 Expose Context                                                          */
  /* -------------------------------------------------------------------------- */
  const value = useMemo(
    () => ({
      publicProfile,
      allPublicStudios,
      loading,
      error,
      isPublished: publicProfile?.is_active_public || false,
      publishStudio,
      unpublishStudio,
      setMapVisibility,
      refreshOwnProfile: fetchOwnProfile,
      refreshAllStudios: fetchAllPublicStudios,
    }),
    [
      publicProfile,
      allPublicStudios,
      loading,
      error,
      publishStudio,
      unpublishStudio,
      setMapVisibility,
      fetchOwnProfile,
      fetchAllPublicStudios,
    ]
  );

  return (
    <PublicStudioContext.Provider value={value}>
      {children}
    </PublicStudioContext.Provider>
  );
}
